import React, { useState } from "react";
import { Box, Typography, Tab, useTheme } from "@mui/material";
import TabContext from "@mui/lab/TabContext";
import TabList from "@mui/lab/TabList";
import TabPanel from "@mui/lab/TabPanel";
import { useQuery } from "@apollo/client";
import RiskRegister from "../features/risk-register/RiskRegister";
import RiskAssessment from "../features/risk-assessment/RiskAssessment";
import {
  GET_ALL_RISK_ASSESSMENTS,
  GET_RISKS_FROM_REGISTER,
} from "../../../../apis/organization-apis/risk-apis/riskApis";
import { styles } from "../styles/RiskManagement.styles";
import ConfirmSnackbar from "../../../../components/Snackbar/ConfirmSnackbar";
import ConfirmStartAssessmentDialog from "../components/Dialogs/ConfirmStartAssessmentDialog/ConfirmStartAssessmentDialog";
import { RiskAssessmentStatusTitle } from "../utils/enums";
import InformationStepsCard from "../../../../components/InformationStepsCard/InformationStepsCard";

const RiskManagementHome = () => {
  const [tab, setTab] = useState("0");

  const [confirmationAlert, setConfirmationAlert] = useState(false);
  const [snackbarInfo, setSnackbarInfo] = useState(null);
  const {
    data: risksData,
    loading: risksLoading,
    error: risksError,
    refetch: refetchRisks,
  } = useQuery(GET_RISKS_FROM_REGISTER, {
    variables: {
      filter: {},
      page: 1,
      limit: 1000,
    },
    fetchPolicy: "cache-and-network",
    onError: (error) => {
      console.error("Error fetching risks:", error);
    },
  });

  const {
    data: allRiskAssessments,
    loading: allRiskAssessmentsLoading,
    refetch: refetchAssessments,
  } = useQuery(GET_ALL_RISK_ASSESSMENTS);

  const theme = useTheme();

  const handleCancelStartAssessment = () => {
    refetchAssessments();
    setConfirmationAlert(false);
  };

  return (
    <>
      <ConfirmSnackbar snackbarInfo={snackbarInfo} />
      <Box sx={styles.pageContainer}>
        <TabContext value={tab}>
          <Box sx={{ mb: 1 }}>
            <Typography
              variant="h5"
              fontWeight="600"
              color={theme.customColors.text.strong}
              sx={{ mb: 0.5 }}
            >
              {tab === "0" ? "Risk Register" : "Risk Assessment"}
            </Typography>
          </Box>
          {/* Information Steps Card */}
          <Box sx={{ mb: 3, mt: 3 }}>
            <InformationStepsCard
              heading="**What should I do on this page?**"
              subtitle="This page helps you manage organizational risks through a risk register and conduct periodic risk assessments."
              steps={[
                "**Risk Register** tab: View and manage all identified risks in your organization. Add, edit, and track risks throughout their lifecycle.",

                "**Risk Assessment** tab: Conduct periodic risk assessments by reviewing existing risks, evaluating their current status, and updating risk levels based on current organizational context.",

                "Use the risk register to maintain a comprehensive list of all risks, including their descriptions, likelihood, impact, and mitigation strategies.",

                "Start a new **risk assessment** to systematically review all risks and update their status. This creates a snapshot of your risk posture at a specific point in time.",

                "Track risk assessment history to see how your risk landscape has changed over time and demonstrate risk management practices to auditors.",
              ]}
              footerText="💡 Regular risk assessments help you stay proactive about managing organizational risks and demonstrate due diligence to stakeholders."
              collapsible={true}
              defaultExpanded={false}
            />
          </Box>
          <Box
            sx={{
              borderBottom: 1,
              borderColor: theme.customColors.border.main,
              mb: 2,
            }}
          >
            <TabList
              onChange={(_, v) => setTab(v)}
              aria-label="Risk Management Tabs"
            >
              <Tab label="Risk Register" value="0" />
              <Tab label="Risk Assessment" value="1" />
            </TabList>
          </Box>
          <TabPanel value="0" sx={{ p: 0 }}>
            <RiskRegister
              risksData={risksData}
              risksLoading={risksLoading}
              risksRefetch={refetchRisks}
              error={risksError}
              onStartAssessment={() => setConfirmationAlert(true)}
              hasDraftAssessment={
                allRiskAssessments?.getAllRiskAssessments?.data[0]
                  ?.status_title === RiskAssessmentStatusTitle.DRAFT
              }
            />
          </TabPanel>
          <TabPanel value="1" sx={{ p: 0 }}>
            <RiskAssessment
              allRiskAssessments={allRiskAssessments}
              allRiskAssessmentsLoading={allRiskAssessmentsLoading}
              refetchAssessments={refetchAssessments}
              onStartAssessment={() => setConfirmationAlert(true)}
            />
          </TabPanel>
        </TabContext>
      </Box>

      <ConfirmStartAssessmentDialog
        open={confirmationAlert}
        onClose={(success) => {
          handleCancelStartAssessment();
          if (success) setTab("1");
          else setTab("0");
        }}
        setSnackbarInfo={setSnackbarInfo}
      />
    </>
  );
};

export default RiskManagementHome;
